/** App-owned provenance. Model arguments cannot mint a turn or reserve compute. */
export interface AutomationTurn {
  id: string
  signal: AbortSignal
  state: 'open' | 'finished' | 'cancelled'
  runs: number
  dispatched: Map<string, Promise<unknown>>
  context?: object
  expectedSignature?: string
  assertAccess?: () => void
}

export interface GraphApiInvocation {
  turn: AutomationTurn
  manualEpoch: number
}

let context: { owner: object; signature: () => string; cleanup: () => void } | null = null
let executingTurn: AutomationTurn | undefined
let graphApiInvocation: GraphApiInvocation | undefined
const activeTurns = new Set<AutomationTurn>()
export const currentAutomationTurn = () => executingTurn
let pendingActions = 0
let manualEpoch = 0
let authored: { signature: string; turn: AutomationTurn } | undefined
let suppressedSignature: string | undefined
const listeners = new Set<() => void>()
let version = 0
const notify = () => {
  version++
  listeners.forEach((listener) => listener())
}

function clearProvenanceAfterDivergence(signature: string): void {
  if (pendingActions > 0) return
  if (authored && authored.signature !== signature) authored = undefined
  if (suppressedSignature !== undefined && suppressedSignature !== signature) {
    suppressedSignature = undefined
  }
}

export function invalidateExecutionAnalysis(): void {
  if (context) clearProvenanceAfterDivergence(context.signature())
  notify()
}

export class AutomationRejectedError extends Error {}

export const subscribeAutomation = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const automationVersion = () => version

/**
 * Conservative user takeover boundary. Trusted editor interaction cancels the
 * current AI turn before React's mutation handler runs. The settled signature
 * is tombstoned by withGraphAction so a landed AI edit cannot later auto-run as
 * an unbudgeted manual action.
 */
export function noteManualGraphInteraction(): void {
  const openTurns = [...activeTurns].filter((turn) => turn.state === 'open')
  const hasAiProvenance =
    openTurns.length > 0 || executingTurn?.state === 'open' || authored !== undefined
  if (!hasAiProvenance) return

  manualEpoch++
  for (const turn of openTurns) {
    turn.state = 'cancelled'
    activeTurns.delete(turn)
  }
  if (executingTurn?.state === 'open') executingTurn.state = 'cancelled'
  if (authored) {
    authored.turn.state = 'cancelled'
    authored = undefined
  }
  if (context) suppressedSignature = context.signature()
  notify()
}

const TRUSTED_EDITOR_EVENTS = [
  'pointerdown',
  'keydown',
  'beforeinput',
  'input',
  'click',
  'drop',
] as const

function listenForTrustedEditorInteraction(): () => void {
  if (typeof document === 'undefined') return () => undefined
  const takeOver = (event: Event) => {
    if (event.isTrusted) noteManualGraphInteraction()
  }
  for (const eventName of TRUSTED_EDITOR_EVENTS) {
    document.addEventListener(eventName, takeOver, true)
  }
  return () => {
    for (const eventName of TRUSTED_EDITOR_EVENTS) {
      document.removeEventListener(eventName, takeOver, true)
    }
  }
}

export function bindExecutionContext(owner: object, signature: () => string): () => void {
  context?.cleanup()
  context = { owner, signature, cleanup: listenForTrustedEditorInteraction() }
  authored = undefined
  suppressedSignature = undefined
  notify()
  return () => {
    if (context?.owner !== owner) return
    context.cleanup()
    context = null
    authored = undefined
    suppressedSignature = undefined
    for (const turn of activeTurns) turn.state = 'cancelled'
    activeTurns.clear()
    notify()
  }
}

export function createAutomationTurn(
  id: string,
  signal: AbortSignal,
  assertAccess?: () => void,
): AutomationTurn {
  const turn: AutomationTurn = {
    id,
    signal,
    state: 'open',
    runs: 0,
    dispatched: new Map(),
    assertAccess,
    context: context?.owner,
    expectedSignature: context?.signature(),
  }
  activeTurns.add(turn)
  signal.addEventListener('abort', () => finishAutomationTurn(turn, false), { once: true })
  return turn
}

export function assertAutomationTurnCurrent(
  turn: AutomationTurn,
  expectedManualEpoch?: number,
  checkRevision = true,
  options?: { allowHeadless?: boolean },
): void {
  if (
    turn.signal.aborted ||
    turn.state === 'cancelled' ||
    (expectedManualEpoch !== undefined && expectedManualEpoch !== manualEpoch)
  ) {
    throw new AutomationRejectedError('AI task stopped; dispatched backend work may still finish.')
  }
  const allowHeadless = options?.allowHeadless === true
  if (!allowHeadless && (!context || (turn.context && turn.context !== context.owner))) {
    throw new AutomationRejectedError('Strategy editor changed; start a new task.')
  }
  if (
    !allowHeadless &&
    checkRevision &&
    turn.expectedSignature !== undefined &&
    (!context || turn.expectedSignature !== context.signature())
  ) {
    throw new AutomationRejectedError(
      'Graph changed outside this AI task; read the new graph in a new turn.',
    )
  }
  turn.assertAccess?.()
  if (context) {
    turn.context ??= context.owner
    turn.expectedSignature ??= context.signature()
  }
}

export function currentGraphApiInvocation(): GraphApiInvocation | undefined {
  return graphApiInvocation
}

/**
 * Tag only the synchronous public-API invocation. The API proxy/queue captures
 * this value before a Promise is returned; the tag never leaks across awaits.
 */
export function withGraphApiInvocation<T>(turn: AutomationTurn, action: () => T): T {
  assertAutomationTurnCurrent(turn)
  const previous = graphApiInvocation
  graphApiInvocation = { turn, manualEpoch }
  try {
    return action()
  } finally {
    graphApiInvocation = previous
  }
}

export async function withGraphAction<T>(
  turn: AutomationTurn,
  action: () => T | Promise<T>,
): Promise<T> {
  assertAutomationTurnCurrent(turn)
  if (executingTurn) throw new Error('Another graph action is already in progress.')
  const startedManualEpoch = manualEpoch
  executingTurn = turn
  pendingActions++
  notify()
  try {
    const result = await action()
    assertAutomationTurnCurrent(turn, startedManualEpoch, false)
    return result
  } finally {
    if (context && context.owner === turn.context) {
      const settledSignature = context.signature()
      const takenOver =
        turn.signal.aborted || turn.state === 'cancelled' || startedManualEpoch !== manualEpoch
      authored = undefined
      if (takenOver) {
        suppressedSignature = settledSignature
      } else {
        turn.expectedSignature = settledSignature
        suppressedSignature = undefined
        authored = { signature: settledSignature, turn }
      }
    }
    pendingActions--
    executingTurn = undefined
    notify()
  }
}

export function finishAutomationTurn(turn: AutomationTurn, completed: boolean): void {
  if (turn.state !== 'cancelled') {
    turn.state = completed && !turn.signal.aborted ? 'finished' : 'cancelled'
  }
  activeTurns.delete(turn)
  notify()
}

export function autoRunPolicy(signature: string): {
  blocked: boolean
  skip: boolean
  turn?: AutomationTurn
} {
  clearProvenanceAfterDivergence(signature)
  const provenance = authored?.signature === signature ? authored : undefined
  const turn = provenance?.turn
  return {
    blocked: pendingActions > 0 || turn?.state === 'open',
    skip:
      suppressedSignature === signature ||
      (!!turn &&
        (turn.signal.aborted || turn.state === 'cancelled' || turn.dispatched.has(signature))),
    turn,
  }
}

/** Called after graph validation, immediately before actual network dispatch. */
export function dispatchSingleRun<T>(
  signature: string,
  dispatch: () => Promise<T>,
  cause: AutomationTurn | null = executingTurn ?? null,
  options?: { allowHeadless?: boolean },
): Promise<T> {
  if (!cause) return dispatch()
  assertAutomationTurnCurrent(cause, undefined, true, options)
  const previous = cause.dispatched.get(signature)
  if (previous) return previous as Promise<T>
  if (cause.runs >= 3) {
    throw new AutomationRejectedError('AI single-run budget exhausted (3 runs per turn).')
  }
  cause.runs++
  const promise = Promise.resolve().then(() => {
    assertAutomationTurnCurrent(cause, undefined, true, options)
    return dispatch()
  })
  cause.dispatched.set(signature, promise)
  authored = { signature, turn: cause }
  suppressedSignature = undefined
  notify()
  return promise
}
